<template>
  <!-- Answers a plan-gate refusal (utils/plan-errors.js) with our own translated text -->
  <v-dialog v-model="dialog" max-width="520">
    <v-card v-if="detail" class="rounded-lg">
      <v-card-title class="d-flex align-center flex-nowrap pt-6 px-6">
        <v-icon :color="isSuspended ? 'error' : 'warning'" class="me-3">{{ isSuspended ? "mdi-lock-alert-outline" : "mdi-crown-outline" }}</v-icon>
        <span class="text-h6 font-weight-bold dark--text plan-upgrade-title">{{ title }}</span>
      </v-card-title>
      <v-card-text class="px-6 pt-2">
        <p class="text-body-1 dark--text mb-0">{{ body }}</p>
        <div v-if="usageLine" class="plan-upgrade-usage text-body-2 font-weight-bold mt-3">{{ usageLine }}</div>
      </v-card-text>
      <v-card-actions class="px-6 pb-5">
        <v-spacer></v-spacer>
        <v-btn text class="text-capitalize" @click="dialog = false">{{ $t('common.close') }}</v-btn>
        <v-btn v-if="isSuspended" color="primary" depressed class="text-capitalize" @click="go({ name: 'BillsAndPayments' })">
          {{ $t('bills-and-payments.billsAndPayments') }}
        </v-btn>
        <v-btn v-else-if="planBackend" color="primary" depressed class="text-capitalize" @click="viewMyPlan">{{ $t('saasPlan.viewMyPlan') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { PLAN_ERROR_EVENT } from "../../utils/plan-errors";
import { loadPlanDetails, featureLabel, limitLabel } from "../../utils/saas-catalog";
import { entitlementsState } from "../../utils/entitlements";

export default {
  name: "PlanUpgradeDialog",
  props: {
    // set where something else (App.vue's suspended overlay) already answers ACCOUNT_SUSPENDED
    overlayHandlesSuspension: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      dialog: false,
      detail: null, // { type, feature, limit, used, max }
      catalog: null,
    };
  },
  computed: {
    // My plan exists only where the SaaS backend serves plans
    planBackend() {
      return entitlementsState.supported;
    },
    isSuspended() {
      return !!this.detail && this.detail.type === "ACCOUNT_SUSPENDED";
    },
    isLimit() {
      return !!this.detail && this.detail.type === "PLAN_LIMIT_REACHED";
    },
    title() {
      if (this.isSuspended) return this.$t("saasPlan.suspendedTitle");
      if (this.isLimit) return this.$t("saasPlan.limitReachedTitle");
      return this.$t("saasPlan.featureNotInPlanTitle");
    },
    body() {
      const detail = this.detail || {};
      if (this.isSuspended) return this.$t("saasPlan.suspendedMessage");
      if (this.isLimit) {
        return detail.limit
          ? this.$t("saasPlan.limitReachedBody", { limit: limitLabel(this.catalog, detail.limit, this.$i18n.locale) })
          : this.$t("saasPlan.limitReachedMessage");
      }
      return detail.feature
        ? this.$t("saasPlan.featureNotInPlanBody", { feature: featureLabel(this.catalog, detail.feature, this.$i18n.locale) })
        : this.$t("saasPlan.featureNotInPlanMessage");
    },
    usageLine() {
      const detail = this.detail;
      if (!this.isLimit || typeof detail.used !== "number" || typeof detail.max !== "number") return "";
      return this.$t("saasPlan.usedOfMax", { used: detail.used, max: detail.max });
    },
  },
  mounted() {
    this.$root.$on(PLAN_ERROR_EVENT, this.open);
  },
  beforeDestroy() {
    this.$root.$off(PLAN_ERROR_EVENT, this.open);
  },
  methods: {
    open(detail) {
      // one at a time: a burst of refusals from one page opens it once
      if (!detail || this.dialog) return;
      if (detail.type === "ACCOUNT_SUSPENDED" && this.overlayHandlesSuspension) return;
      this.detail = detail;
      this.dialog = true;
      // labels in the current language, and a fresh copy of the entitlements (the refusal may
      // mean the copy this browser holds is out of date)
      if (!this.planBackend) return;
      loadPlanDetails({ force: true }).then(result => {
        if (result.success && result.data && result.data.catalog) this.catalog = result.data.catalog;
      });
    },
    go(location) {
      this.dialog = false;
      this.$router.push(location).catch(() => {});
    },
    viewMyPlan() {
      const feature = this.detail && this.detail.type === "FEATURE_NOT_IN_PLAN" ? this.detail.feature : null;
      this.go(feature ? { name: "MyPlan", query: { locked: feature } } : { name: "MyPlan" });
    },
  },
};
</script>

<style scoped>
.plan-upgrade-title {
  word-break: normal;
  line-height: 1.4;
}

.plan-upgrade-usage {
  display: inline-block;
  padding: 6px 12px;
  border-radius: 8px;
  background-color: rgba(245, 158, 11, 0.12);
  color: #92400e;
}
</style>
